import {
  Button,
  Col,
  DatePicker,
  Row,
  Tooltip,
  Typography,
  Flex,
  Select,
  message,
} from "antd";
import { useState, useContext, useEffect } from "react";
import { IndexContext } from "../../context/IndexContext";
import useFetch from "../../hooks/useFetch";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import { DownloadOutlined } from "@ant-design/icons";

export default function ReportPage() {
  const [fetchWrapper] = useFetch();

  // load index context
  const { notificationApi } = useContext(IndexContext);

  // corporate selection
  const [corporateList, setCorporateList] = useState([]);
  const [corporateSelected, setCorporateSelected] = useState(null);
  const [getCorporateSelectionLoading, setGetCorporateSelectionLoading] =
    useState(false);

  // site selection
  const [siteList, setSiteList] = useState([]);
  const [siteSelected, setSiteSelected] = useState(null);
  const [getSiteSelectionLoading, setGetSiteSelectionLoading] = useState(false);

  // selected month year
  const [inventoryYear, setInventoryYear] = useState(dayjs().startOf("year"));
  const [startDate, setStartDate] = useState(dayjs().startOf("year"));
  const [endDate, setEndDate] = useState(dayjs().endOf("year"));

  const [downloading, setDownloading] = useState(false);

  const columns = [
    {
      title: "Template Name",
      dataIndex: "TemplateName",
      key: "TemplateName",
    },
    {
      title: "Action",
      key: "Action",
      render: (_, record) => (
        <Tooltip title="Download">
          <Button
            loading={downloading}
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => {
              if (siteSelected.length === 0) {
                message.error("Please select site");
              } else {
                downloadReport();
              }
            }}
            disabled={!siteSelected || !corporateSelected}
          />
        </Tooltip>
      ),
    },
  ];

  const data = [
    {
      key: 1,
      TemplateName: "Company GHG Inventory Report",
    },
  ];

  /* API to get corporate selection */
  function getCorporateSelection() {
    setGetCorporateSelectionLoading(true);
    fetchWrapper({
      endpoint_url: "reports/getCorporateSelection",
      onSuccess: (response) => {
        setGetCorporateSelectionLoading(false);

        setCorporateList(response.data);
        if (response.data.length > 0) {
          setCorporateSelected(response.data[0].value);
        }
      },
    });
  }

  /* API to get site selection */
  function getSiteSelection() {
    setGetSiteSelectionLoading(true);
    fetchWrapper({
      endpoint_url: "reports/getSiteSelection",
      body: {
        user_group_ID: corporateSelected,
      },
      onSuccess: (response) => {
        setGetSiteSelectionLoading(false);

        setSiteList(response.data);
        if (response.data.length > 0) {
          // setSiteSelected(response.data[0].value);
          setSiteSelected(response.data.map((item) => item.value));
        }
      },
    });
  }

  function downloadReport() {
    setDownloading(true);

    fetchWrapper({
      method: "POST",
      endpoint_url: "reports/downloadReport",
      json_output: false,
      params: {
        corporate_id: corporateSelected,
        site_id_list: siteSelected,
        inventory_year: inventoryYear.format("YYYY"),
        start_date: startDate.format("YYYY-MM-DD"),
        end_date: endDate.format("YYYY-MM-DD"),
      },
      onSuccess: (response) => {
        // returned a json response.
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Report_${dayjs().format("YYYYMMDD_HHmmss")}.docx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        } else {
          response.json().then((response_data) => {
            const messageContent = response_data.data.missing_data
              ? response_data.data.missing_data
                  .map((item) => {
                    return (
                      `- [${item.YearMonth}] ` +
                      `Scope: ${item.Scope}, ` +
                      `Type: ${item.ScopeType}, ` +
                      `Site: ${item.SiteName}, ` +
                      `Source: ${item.EmissionSource}, ` +
                      `Dimension: ${item.Dimension}` +
                      (item.CategoryType
                        ? `, Category: ${item.CategoryType}`
                        : "")
                    );
                  })
                  .join("<br>")
              : null;

            notificationApi.error({
              message: response_data.message,
              description: messageContent ? (
                <span dangerouslySetInnerHTML={{ __html: messageContent }} />
              ) : null,
            });
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({ message: response.message });
      },
      onFetchError: (error) => {
        notificationApi.error({
          message: "Server Error",
          descripton: error.message,
        });
      },
      onFinish: () => {
        setDownloading(false);
      },
    });
  }

  useEffect(() => {
    getCorporateSelection();
  }, []);

  useEffect(() => {
    if (corporateSelected) {
      getSiteSelection();
    }
  }, [corporateSelected]);

  return (
    <>
      <Flex align="center">
        <p className="mr-2">Corporate:</p>
        <Select
          style={{ minWidth: "calc(100vw - 92px)", textAlign: "left" }}
          loading={getCorporateSelectionLoading}
          showSearch
          value={corporateSelected}
          options={corporateList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setCorporateSelected(value);
          }}
        />
      </Flex>

      <Flex align="center">
        <p className="mr-2">Site:</p>
        <Select
          mode="multiple"
          style={{ minWidth: "calc(100vw - 54px)", textAlign: "left" }}
          loading={getSiteSelectionLoading}
          showSearch
          value={siteSelected}
          options={siteList}
          optionFilterProp="label"
          labelRender={(value) => <>{value.label}</>}
          onChange={(value) => {
            setSiteSelected(value);
          }}
        />
      </Flex>

      <Row className="pb-2" gutter={[8, 8]}>
        <Col
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <div>Year:</div>
          <DatePicker
            picker="year"
            value={inventoryYear}
            style={{ width: 200, marginLeft: "8px" }}
            onChange={(value) => {
              if (value) {
                setInventoryYear(value);

                // Set startDate to Jan 1 and endDate to Dec 31 of the selected year
                const startOfYear = value.clone().startOf("year");
                const endOfYear = value.clone().endOf("year");

                setStartDate(startOfYear);
                setEndDate(endOfYear);
              }
            }}
          />
        </Col>

        <Col
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <div>Start Date:</div>
          <DatePicker
            picker="date"
            value={startDate}
            style={{ width: 200, marginLeft: "8px" }}
            onChange={(value) => {
              if (value) setStartDate(value);
            }}
            disabledDate={(current) => {
              // Disable dates not in the selected inventory year
              if (!inventoryYear) return false; // allow all if inventoryYear not selected
              const year = inventoryYear.year();
              if (current.year() !== year) return true;

              // Disable dates after endDate
              if (endDate && current.isAfter(endDate, "day")) return true;

              return false;
            }}
          />
        </Col>

        <Col
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <div>End Date:</div>
          <DatePicker
            picker="date"
            value={endDate}
            style={{ width: 200, marginLeft: "8px" }}
            onChange={(value) => {
              if (value) setEndDate(value);
            }}
            disabledDate={(current) => {
              // Disable dates not in the selected inventory year
              if (!inventoryYear) return false;
              const year = inventoryYear.year();
              if (current.year() !== year) return true;

              // Disable dates before startDate
              if (startDate && current.isBefore(startDate, "day")) return true;

              return false;
            }}
          />
        </Col>
      </Row>
      <DataTable table_column={columns} table_data={data} scroll_width={1500} />
    </>
  );
}
